import { useState, useEffect, useCallback } from "react";
import { Loader2, Download, FileText, AlertTriangle, Check, CornerUpLeft } from "lucide-react";

// PGA/LPGA COMPLETION SUBMISSION — the admin screen (Randall, 2026-09-22).
//
// Three things a person needs from it: who has completed, one fitter's statement, and the
// roster. It READS and RENDERS. There is no button here that sends anything, because nothing
// in the system can: no route transmits, and education_pga_submission has no send path for one
// to call. Nobody has confirmed Henry-Griffitts' standing as a recognised provider with either
// association, so the documents exist and the filing does not.
//
// THE CAUTION LIVES HERE, ON PURPOSE. It came off the fitter-facing statement on 22 September
// -- a document that opens by hedging about the association's own decision reads as a
// disclaimer rather than a record. It did not disappear; it moved to the surfaces Henry-
// Griffitts looks at, which is this one. A person pulling a statement should be able to tell at
// a glance that nothing has been filed, and the banner below is that glance.

// THE APPROVAL GATE (Randall, 2026-10-02): "Nothing sends to the PGA or LPGA without me seeing it first and approving
// for send after someone completes it." The queue sits at the top of this screen. He opens a submission and reads THE
// DOCUMENT ITSELF, framed below exactly as it renders -- the same HTML the PDF is made from. He approves it or returns
// it with a note. The server decides who may: the buttons show for his account only, and the routes refuse anyone else
// whatever the screen shows. The PDF link and the roster hold only what he has approved.

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

async function postJSON(path, body) {
  const res = await fetch(`${API_URL}${path}`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" }, body: JSON.stringify(body || {}),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : `Request failed (${res.status})`);
  return d;
}

export const GATE_STATUS = {
  awaiting_approval: "Awaiting his approval",
  approved: "Approved for a person to file",
  returned: "Returned",
  // ROUTING BY MEMBERSHIP (Randall, 2026-10-05)
  nothing_to_send: "Completed: a member of neither body, nothing to send",
  superseded: "Rebuilt for the fitter's body once they gave it",
};
// A submission that cannot be authorised says so, and why, and never shows an approve button.
const blocked = (x) => x.sendable === false && x.status === "awaiting_approval";
const when = (iso) => (iso ? new Date(iso).toLocaleString() : "");

async function getJSON(path) {
  const res = await fetch(`${API_URL}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.detail || `Request failed (${res.status})`);
  }
  return res.json();
}

// THE BANNER (Randall, 2026-10-05). Shown on every page to the approver while something waits for him. Says how many
// can be authorised and how many are blocked (no association on file), so a blocked one never reads as work he can do.
export function SubmissionsWaitingBanner({ onOpen }) {
  const [w, setW] = useState(null);
  useEffect(() => {
    let live = true;
    getJSON("/api/education/admin/submissions/waiting").then((d) => { if (live) setW(d); }).catch(() => {});
    return () => { live = false; };
  }, []);
  if (!w || !w.viewer_can_decide || !w.awaiting) return null;
  return (
    <div className="admin-note" data-testid="gate-waiting-banner" role="status" style={{ margin: "8px 16px" }}>
      <AlertTriangle size={14} aria-hidden="true" />{" "}
      {w.awaiting_authorisable
        ? `${w.awaiting_authorisable} completion submission${w.awaiting_authorisable === 1 ? " is" : "s are"} waiting for your authorisation.`
        : ""}
      {w.awaiting_blocked
        ? ` ${w.awaiting_blocked} cannot be authorised yet: no association on file.` : ""}
      {w.awaiting_nudges
        ? ` ${w.awaiting_nudges} draft nudge${w.awaiting_nudges === 1 ? " is" : "s are"} waiting for your approval.` : ""}{" "}
      <button className="btn btn-text btn-sm" onClick={onOpen} data-testid="gate-waiting-open">Open the queue</button>
    </div>
  );
}

export default function PgaSubmissionPanel() {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");   // loading | ok | failed
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);          // the statement being previewed
  const [busy, setBusy] = useState("");
  const [queue, setQueue] = useState(null);        // the approval queue
  const [sub, setSub] = useState(null);            // the submission being read, with its document
  const [note, setNote] = useState("");
  const [gateErr, setGateErr] = useState("");

  const load = useCallback(async () => {
    setState("loading");
    try {
      // the queue first: reading it is what queues anyone who has completed
      setQueue(await getJSON("/api/education/admin/submissions"));
      setData(await getJSON("/api/education/admin/pga/completions"));
      setState("ok");
    } catch (e) {
      // A FAILED READ IS "I DO NOT KNOW", never "nobody has completed the course" -- rendering
      // an empty roster on an expired session would read as a pilot nobody finished.
      setError(e.message);
      setState("failed");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const preview = async (row) => {
    setBusy(row.email);
    try {
      const d = await getJSON(
        `/api/education/admin/pga/statement/${encodeURIComponent(row.user_id || row.email)}`);
      setOpen({ row, text: d.text, record: d.record });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  };

  const openSubmission = async (id) => {
    setGateErr(""); setNote("");
    try { setSub(await getJSON(`/api/education/admin/submissions/${id}`)); }
    catch (e) { setGateErr(e.message); }
  };
  const decide = async (action) => {
    setGateErr(""); setBusy(action);
    try {
      await postJSON(`/api/education/admin/submissions/${sub.submission_id}/${action}`,
                     action === "approve" ? { document_hash: sub.document.hash } : { note });
      setSub(null); setNote("");
      await load();
    } catch (e) { setGateErr(e.message); }
    finally { setBusy(""); }
  };
  const requeue = async (id) => {
    setGateErr("");
    try { await postJSON(`/api/education/admin/submissions/${id}/requeue`); await load(); }
    catch (e) { setGateErr(e.message); }
  };

  if (state === "loading") {
    return <div className="admin-note"><Loader2 className="spin" size={16} /> Loading completions…</div>;
  }
  if (state === "failed") {
    return (
      <div className="admin-note" data-testid="pga-error">
        <AlertTriangle size={16} aria-hidden="true" /> {error}
        <button className="btn btn-text btn-sm" onClick={load}>Try again</button>
      </div>
    );
  }

  const rows = data?.rows || [];
  const subs = queue?.submissions || [];
  const approvedIds = new Set(subs.filter((x) => x.status === "approved").map((x) => x.user_id));
  return (
    <div data-testid="pga-panel">
      <div className="admin-header">
        <div>
          <div className="section-title">PGA / LPGA completion submission</div>
          <p className="section-sub">
            {data.ready_count} fitter{data.ready_count === 1 ? "" : "s"} ready to submit
            {data.skipped_count ? ` · ${data.skipped_count} not complete` : ""}
            {data.missing_member_numbers
              ? ` · ${data.missing_member_numbers} without a member number` : ""}
          </p>
        </div>
        <div className="admin-actions">
          <a className="btn btn-secondary" href={`${API_URL}/api/education/admin/pga/roster.csv`}
             data-testid="pga-roster-csv" title="Holds only the fitters whose submission has been approved">
            <Download size={15} aria-hidden="true" /> Roster CSV (approved only)
          </a>
        </div>
      </div>

      {/* ------------------------------------------------ THE APPROVAL QUEUE */}
      <div className="admin-panel" data-testid="gate-queue">
        <div className="section-title">Awaiting approval before anything is filed</div>
        <p className="admin-note">
          {queue?.note} {queue?.viewer_can_decide
            ? "You are signed in as the approver."
            : "Only the approver can approve or return a submission; this account can read the queue."}
        </p>
        {gateErr && (
          <p className="admin-note" data-testid="gate-error"><AlertTriangle size={14} aria-hidden="true" /> {gateErr}</p>
        )}
        {subs.length === 0 ? (
          <p className="admin-note" data-testid="gate-empty">Nothing is in the queue. A submission appears here when a fitter completes the course.</p>
        ) : (
          <table className="admin-table" data-testid="gate-table">
            <thead><tr><th>Fitter</th><th>For</th><th>Body</th><th>Queued</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {subs.map((x) => {
                const last = (x.decisions || [])[(x.decisions || []).length - 1];
                return (
                  <tr key={x.submission_id}>
                    <td>{x.holder_name || <i>name not recorded</i>}<br /><small>{x.holder_email}</small></td>
                    <td>{x.unit_label}{x.version > 1 ? ` (version ${x.version})` : ""}</td>
                    <td>{x.body_label}</td>
                    <td>{when(x.created_at)}</td>
                    <td>
                      <b>{GATE_STATUS[x.status] || x.status}</b>
                      {blocked(x) && <><br /><small data-testid="gate-blocked">Cannot be authorised: no association on file</small></>}
                      {x.status === "awaiting_approval" && x.sendable !== false && !x.destination && x.unit !== "credit_statement" && (
                        <><br /><small data-testid="gate-nowhere">Nowhere to send it yet</small></>
                      )}
                      {last && <><br /><small>{last.by_email} · {when(last.at)}{last.note ? ` · ${last.note}` : ""}</small></>}
                    </td>
                    <td>
                      <button className="btn btn-text btn-sm" onClick={() => openSubmission(x.submission_id)}
                              data-testid="gate-open"><FileText size={14} aria-hidden="true" /> Read the document</button>
                      {x.status === "returned" && !x.replaced_by && x.unit !== "credit_statement" && x.unit !== "fitter_nudge" && (
                        <button className="btn btn-text btn-sm" onClick={() => requeue(x.submission_id)}
                                data-testid="gate-requeue">Rebuild and queue again</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {sub && (
          <div className="admin-panel" data-testid="gate-document">
            <div className="admin-header">
              <div className="section-title">
                {sub.holder_name || sub.holder_email} — {GATE_STATUS[sub.status] || sub.status}
              </div>
              <div className="admin-actions">
                <button className="btn btn-text btn-sm" onClick={() => setSub(null)}>Close</button>
              </div>
            </div>
            {(sub.blockers || []).map((b) => (
              <p className="admin-note" key={b} data-testid="gate-blocker">
                <AlertTriangle size={14} aria-hidden="true" /> <b>Cannot be authorised.</b> {b}
              </p>
            ))}
            {sub.note && <p className="admin-note" data-testid="gate-sub-note">{sub.note}</p>}
            {(sub.warnings || []).map((w) => (
              <p className="admin-note" key={w}><AlertTriangle size={14} aria-hidden="true" /> {w}</p>
            ))}
            {sub.destination && sub.status === "awaiting_approval" && (
              <p className="admin-note">{sub.unit === "fitter_nudge" ? `Once approved, it goes to ${sub.destination}.` : `Once authorised, a person files it with: ${sub.destination}.`}</p>
            )}
            {sub.unit === "fitter_nudge" && sub.nudge?.sent_at && (
              <p className="admin-note" data-testid="gate-nudge-sent">Sent to {sub.nudge.to} on {when(sub.nudge.sent_at)}.</p>
            )}
            {sub.unit === "fitter_nudge" ? (
              <p className="admin-note" data-testid="gate-nudge-note">
                This is the draft email itself, as the fitter would receive it. It was written when Nudge was
                pressed on the Training Status screen and has not changed since.
              </p>
            ) : (
              <p className="admin-note">
                This is the document itself, as {sub.body ? `the ${sub.body_label}` : "an association"} would
                receive it. It was built when the fitter completed and has not changed since.
              </p>
            )}
            {/* THE DOCUMENT, NOT A SUMMARY OF IT: the stored HTML, framed and inert. */}
            <iframe title="The submission as it would be received" sandbox="" srcDoc={sub.document.html}
                    style={{ width: "100%", height: 720, border: "1px solid #ccc", background: "#fff" }}
                    data-testid="gate-document-frame" />
            {(sub.decisions || []).map((d) => (
              <p className="admin-note" key={d.at}>
                {d.action === "not_sent"
                  ? `On ${when(d.at)}: ${d.note}`
                  : <>{({ approved: "Approved", edited: "Edited" })[d.action] || "Returned"} by {d.by_email} on {when(d.at)}
                    {d.note ? `: ${d.note}` : ""}</>}
              </p>
            ))}
            {sub.status === "awaiting_approval" && sub.viewer_can_decide && !blocked(sub) && (
              <div data-testid="gate-decide">
                <button className="btn btn-primary" onClick={() => decide("approve")} disabled={!!busy}
                        data-testid="gate-approve"><Check size={15} aria-hidden="true" /> {sub.unit === "fitter_nudge" ? "Approve this draft" : `Authorise this document for the ${sub.body_label}`}</button>
                <div style={{ marginTop: 12 }}>
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} style={{ width: "100%" }}
                            placeholder="What needs to change before this can go" aria-label="Note for a returned submission"
                            data-testid="gate-note" />
                  <button className="btn btn-secondary" onClick={() => decide("return")} disabled={!!busy || !note.trim()}
                          data-testid="gate-return"><CornerUpLeft size={15} aria-hidden="true" /> Return with this note</button>
                </div>
              </div>
            )}
            {sub.status === "awaiting_approval" && !sub.viewer_can_decide && (
              <p className="admin-note">This account can read the document. Only the approver can approve or return it.</p>
            )}
          </div>
        )}
      </div>

      {/* THE CAUTION, on the surface HG looks at. */}
      <div className="admin-note" data-testid="pga-status-note">
        <AlertTriangle size={16} aria-hidden="true" /> <b>{data.status_note}</b>
      </div>

      <p className="admin-note">
        Completion means {data.completion_basis
          ? "every chapter finished and the final exam passed" : ""}. Online course hours only;
        in-person hours are not included.
      </p>

      {rows.length === 0 ? (
        <p className="admin-note" data-testid="pga-empty">
          Nobody has completed the course yet, so there is nothing to submit.
          {data.skipped_count
            ? ` ${data.skipped_count} fitter(s) are part-way through.` : ""}
        </p>
      ) : (
        <table className="admin-table" data-testid="pga-table">
          <thead>
            <tr>
              <th>Fitter</th><th>Association</th><th>Member number</th>
              <th>Completed</th><th>Online hours</th><th>Statement</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.email}>
                <td>{r.name || <i>name not recorded</i>}<br /><small>{r.email}</small></td>
                <td>{r.association || "—"}</td>
                <td>
                  {r.member_number || (
                    <span className="badge badge-warn" data-testid="pga-missing-number">
                      not recorded
                    </span>
                  )}
                </td>
                <td>{r.completed_on}</td>
                <td className="num">{r.online_hours}</td>
                <td>
                  <button className="btn btn-text btn-sm" onClick={() => preview(r)}
                          disabled={busy === r.email}>
                    {busy === r.email ? <Loader2 className="spin" size={14} />
                                      : <FileText size={14} aria-hidden="true" />} View
                  </button>
                  {approvedIds.has(r.user_id) ? (
                    <a className="btn btn-text btn-sm"
                       href={`${API_URL}/api/education/admin/pga/statement/${encodeURIComponent(r.user_id || r.email)}/pdf`}>
                      PDF
                    </a>
                  ) : <small data-testid="pga-pdf-held"> PDF after approval</small>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {data.skipped_count > 0 && (
        <details className="admin-note" data-testid="pga-skipped">
          <summary>{data.skipped_count} not included, and why</summary>
          <ul>
            {(data.skipped || []).map((s) => (
              <li key={s.user_id}><code>{s.user_id}</code> — {s.why}</li>
            ))}
          </ul>
        </details>
      )}

      {open && (
        <div className="admin-panel" data-testid="pga-statement-preview">
          <div className="admin-header">
            <div className="section-title">
              Statement — {open.row.name || open.row.email}
            </div>
            <div className="admin-actions">
              <button className="btn btn-text btn-sm" onClick={() => setOpen(null)}>Close</button>
            </div>
          </div>
          {/* The document as it reads. Reviewed here BEFORE a PDF is produced for anybody. */}
          <pre className="admin-pre">{open.text}</pre>
          <p className="admin-note">
            <AlertTriangle size={14} aria-hidden="true" /> {data.status_note}
          </p>
        </div>
      )}
    </div>
  );
}
